import { Modal } from 'react-bootstrap';
import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-final-form';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { getOrgTermsContent } from '../../../content/utils.js';
import { mapFinalFormErrors } from '../../../lib/utils.js';
import { applyChangeInsight } from '../../../modules/changeInsights.js';
import { retrieveInitiative } from '../../../modules/initiatives.js';
import {
  getEditorSaveDataPromise,
  getReadyStatusPromise,
} from '../../editor/defaultOptions.js';
import CustomizeEmailTemplateForm from './CustomizeEmailTemplateForm.jsx';

const mapErrors = mapFinalFormErrors('Failed to save');

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      retrieveInitiative,
      applyChangeInsight,
    },
    dispatch
  );

const convertToUnderscore = (str) => str.toLowerCase().replace(/ /g, '_');

// const replaceVariables = (str, replacements) => {
//   let result = str;
//   for (const [placeholder, replacement] of Object.entries(replacements)) {
//     const regex = new RegExp(`{{${placeholder}}}`, 'g');
//     result = result.replace(regex, `{{${replacement}}}`);
//   }
//   return result;
// };

const replaceVariables = (str, replacements) => {
  let result = str;
  // Create a map of original placeholders to their temporary placeholders
  const tempMap = {};
  let tempIndex = 0;

  // Replace each original placeholder with a unique temporary placeholder
  for (const [placeholder, replacement] of Object.entries(replacements)) {
    const tempPlaceholder = `__TEMP_${tempIndex}__`;
    tempMap[tempPlaceholder] = `{{${replacement}}}`;
    const regex = new RegExp(`{{${placeholder}}}`, 'g');
    result = result.replace(regex, tempPlaceholder);
    tempIndex++;
  }

  // Replace the temporary placeholders with their corresponding final replacements
  for (const [tempPlaceholder, finalReplacement] of Object.entries(tempMap)) {
    const regex = new RegExp(tempPlaceholder, 'g');
    result = result.replace(regex, finalReplacement);
  }

  return result;
};

const processBlocks = (data, replacements) => {
  const updatedBlocks = data.blocks.map((block) => {
    return {
      ...block,
      data: {
        ...block.data,
        text: replaceVariables(block.data.text, replacements),
      },
    };
  });

  return {
    ...data,
    blocks: updatedBlocks,
  };
};

const replacementsReverse = {
  project_title: `${convertToUnderscore(getOrgTermsContent('project'))}_title`,
  initiative_title: `${convertToUnderscore(getOrgTermsContent('initiative'))}_title`,
};

const replacements = {
  [`${convertToUnderscore(getOrgTermsContent('project'))}_title`]:
    'project_title',
  [`${convertToUnderscore(getOrgTermsContent('initiative'))}_title`]:
    'initiative_title',
};

const DYNAMIC_CUSTOMIZE_EMAIL_VARIABLES = [
  {
    name: '{{first_name}}',
    label: 'First Name',
  },
  {
    name: '{{last_name}}',
    label: 'Last Name',
  },
  {
    name: `{{${convertToUnderscore(getOrgTermsContent('project'))}_title}}`,
    label: `${getOrgTermsContent('project')} Title`,
  },
  {
    name: `{{${convertToUnderscore(getOrgTermsContent('initiative'))}_title}}`,
    label: `${getOrgTermsContent('initiative')} Title`,
  },
  {
    name: '{{feedback_form_title}}',
    label: 'Feedback Form Title',
  },
  {
    name: '{{organization_name}}',
    label: 'Organization Name',
  },
];

const CustomizeEmailTemplateModal = ({
  showModal,
  loading,
  emailSettings,
  handleClose,
  handleClickSendFeedbackForm,
  handleUpdateEmailSetting,
}) => {
  const [initialValues, setInitialValues] = useState({
    emailSubject: '',
    customizeEmailBody: '',
    buttonText: '',
  });

  const formRef = useRef(null);
  const [contentEditorInstance, setContentEditorInstance] = useState(null);

  const onSubmit = (values) => {
    const sendData = {
      subject: replaceVariables(values.emailSubject, replacements),
      button: replaceVariables(values.buttonText, replacements),
    };

    return getReadyStatusPromise(contentEditorInstance).then(() =>
      getEditorSaveDataPromise(contentEditorInstance).then((editorData) => {
        sendData.body = JSON.stringify(processBlocks(editorData, replacements));
        if (values.finish) {
          handleClickSendFeedbackForm(sendData);
        } else {
          handleUpdateEmailSetting({ survey: { email_settings: sendData } });
        }
      })
    );
  };

  const validate = (values) => {
    const errors = {};
    if (!values.emailSubject) {
      errors.emailSubject = 'Email subject is required';
    }

    if (!values.buttonText) {
      errors.buttonText = 'Button text is required';
    }

    return errors;
  };

  useEffect(() => {
    if (emailSettings) {
      setInitialValues({
        emailSubject: replaceVariables(
          emailSettings.subject,
          replacementsReverse
        ),
        customizeEmailBody: replaceVariables(
          emailSettings.body,
          replacementsReverse
        ),
        buttonText: replaceVariables(emailSettings.button, replacementsReverse),
      });
    }
  }, [emailSettings]);

  return (
    <Modal bsSize="large" show={showModal} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Feedback Form Invitation Email</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted m-b-24">
          Customize the email invitation for the feedback form.
          <br />
          You can use dynamic variables like the user's name, feedback form
          title, etc., and apply text formatting as needed.
        </p>
        <div>
          <Form
            initialValues={initialValues}
            onSubmit={onSubmit}
            validate={validate}
            onCancel={handleClose}
            formRef={formRef}
            loading={loading}
            dynamicVariables={DYNAMIC_CUSTOMIZE_EMAIL_VARIABLES}
            setContentEditorInstance={setContentEditorInstance}
            component={CustomizeEmailTemplateForm}
          />
        </div>
      </Modal.Body>
      <Modal.Footer />
    </Modal>
  );
};

export default connect(null, mapDispatchToProps)(CustomizeEmailTemplateModal);
